import type { AxiosError, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import axios from 'axios';

import { GENERAL_ERROR_MESSAGE } from '../../../constants';
import { enumToTitleCase } from '../../../utils/formatting';
import { toCamelCase } from '../../../utils/transform/toCamelCase';
import campaignGraphqlManager from '../index';
import { DCM_TAG_SHEET_PARSE_ENDPOINT } from './constants';
import type { DcmTagSheetParseResult, ParseTagSheetErrorResponse } from './types';

const makeErrorInfoFromResponse = (error: unknown): ParseTagSheetErrorResponse => {
  if (
    axios.isCancel(error) ||
    !axios.isAxiosError<ParseTagSheetErrorResponse>(error) ||
    !error.response?.data?.message
  ) {
    return {
      name: 'Error',
      message: GENERAL_ERROR_MESSAGE,
    };
  }

  return {
    message: error.response.data.message,
    name: error.response.data.name ? enumToTitleCase(error.response.data.name) : '',
    details: error.response.data.details,
  };
};

export const parseTagSheet = async (
  file: File,
  campaignId: string,
  setPercentUploaded: (percent: number) => void,
  cancelTokenSource: CancelTokenSource
): Promise<DcmTagSheetParseResult> => {
  const formData = new FormData();

  formData.append('tag-sheet', file);

  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: { 'campaign-id': campaignId },
    cancelToken: cancelTokenSource.token,
    onUploadProgress: (event: ProgressEvent) => {
      const percent = Math.round((event.loaded * 100) / event.total);
      setPercentUploaded(percent);
    },
  };

  return campaignGraphqlManager
    .post(DCM_TAG_SHEET_PARSE_ENDPOINT, formData, config)
    .then((response: AxiosResponse<DcmTagSheetParseResult>) => toCamelCase<DcmTagSheetParseResult>(response.data))
    .catch((error: AxiosError<ParseTagSheetErrorResponse>) => Promise.reject(makeErrorInfoFromResponse(error)));
};
